// views/Blog.js
import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  Spinner,
  Button,
  Collapse
} from "reactstrap";
import Header from "components/Headers/Header.js";
import moment from "moment";

const API_BASE = process.env.REACT_APP_API_URL || "/api";

const BlogView = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [expandedIds, setExpandedIds] = useState([]);

  useEffect(() => {
    const fetchBlog = async () => {
      try {
        const res = await fetch(`${API_BASE}/blog?limit=10`);
        const json = await res.json();
        setArticles(json.articles || []);

        // Auto-expand first (most recent) article
        if (json.articles?.length > 0) {
          setExpandedIds([0]);
        }
      } catch (err) {
        console.error("Failed to load blog feed:", err);
        setError(true);
        setArticles([]);
      } finally {
        setLoading(false);
      }
    };

    fetchBlog();
  }, []);

  const toggleExpand = (index) => {
    setExpandedIds(prev =>
      prev.includes(index)
        ? prev.filter(id => id !== index)
        : [...prev, index]
    );
  };

  if (loading) {
    return (
      <>
        <Header />
        <Container className="mt--7" fluid>
          <div className="text-center py-5">
            <Spinner color="primary" size="lg" />
            <p className="text-muted mt-3">Loading blog posts...</p>
          </div>
        </Container>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Header />
        <Container className="mt--7" fluid>
          <Row>
            <Col md="12">
              <Card className="shadow">
                <CardBody className="text-center py-5">
                  <i className="fas fa-exclamation-triangle fa-3x text-warning mb-3" />
                  <h3>Unable to Load Blog Feed</h3>
                  <p className="text-muted">
                    The weather blog feed is temporarily unavailable. Please try again later.
                  </p>
                  <Button color="primary" onClick={() => window.location.reload()}>
                    Retry
                  </Button>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </>
    );
  }

  return (
    <>
      <Header />
      <Container className="mt--7" fluid>
        <Row>
          <Col md="12">
            <Card className="shadow">
              <CardHeader className="border-0">
                <div className="d-flex justify-content-between align-items-center flex-wrap">
                  <h3 className="mb-0">
                    <i className="fas fa-cloud-sun mr-2 text-info" />
                    East Street Weather Blog
                  </h3>
                  <a
                    href="https://eaststreetweatherblog.wordpress.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-sm btn-outline-primary"
                  >
                    <i className="fas fa-external-link-alt mr-1" />
                    Visit Blog
                  </a>
                </div>
              </CardHeader>
              <CardBody>
                {articles.length === 0 ? (
                  <div className="text-center py-5">
                    <i className="fas fa-rss fa-3x text-muted mb-3" />
                    <p className="text-muted">No articles available.</p>
                  </div>
                ) : (
                  <div>
                    {articles.map((article, index) => {
                      const isExpanded = expandedIds.includes(index);
                      const publishedDate = moment(article.pubDate).format('MMMM D, YYYY');
                      const publishedAgo = moment(article.pubDate).fromNow();
                      const isRecent = moment().diff(moment(article.pubDate), 'hours') < 48;

                      return (
                        <Card key={index} className={`mb-4 border ${isRecent && index === 0 ? 'border-primary' : ''}`}>
                          <CardHeader className="bg-white">
                            <div
                              className="d-flex justify-content-between align-items-start cursor-pointer"
                              onClick={() => toggleExpand(index)}
                              style={{ cursor: 'pointer' }}
                            >
                              <div className="flex-grow-1">
                                <div className="d-flex align-items-center mb-1">
                                  <h4 className="mb-0 mr-2">{article.title}</h4>
                                  {isRecent && index === 0 && (
                                    <span className="badge badge-primary">New</span>
                                  )}
                                </div>
                                <small className="text-muted">
                                  <i className="fas fa-user mr-1" />
                                  {article.author} •{' '}
                                  <i className="fas fa-calendar mr-1" />
                                  {publishedDate} ({publishedAgo})
                                </small>
                              </div>
                              <Button
                                color="link"
                                className="p-0 ml-3"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleExpand(index);
                                }}
                              >
                                <i className={`fas fa-chevron-${isExpanded ? 'up' : 'down'} fa-lg`} />
                              </Button>
                            </div>
                          </CardHeader>
                          <Collapse isOpen={isExpanded}>
                            <CardBody>
                              {article.imageUrl && (
                                <img
                                  src={article.imageUrl}
                                  alt={article.title}
                                  className="img-fluid mb-4 rounded shadow-sm"
                                  style={{ maxHeight: '500px', width: '100%', objectFit: 'cover' }}
                                />
                              )}
                              <div
                                className="blog-content"
                                dangerouslySetInnerHTML={{ __html: article.content }}
                                style={{
                                  fontSize: '1.05rem',
                                  lineHeight: '1.7',
                                  color: '#525f7f'
                                }}
                              />
                              <hr className="my-4" />
                              <div className="d-flex justify-content-between align-items-center flex-wrap">
                                <div className="text-muted small">
                                  <i className="fas fa-clock mr-1" />
                                  Published {publishedAgo}
                                </div>
                                <a
                                  href={article.link}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="btn btn-sm btn-primary"
                                >
                                  View on WordPress
                                  <i className="fas fa-external-link-alt ml-2" />
                                </a>
                              </div>
                            </CardBody>
                          </Collapse>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default BlogView;
